if (!customElements.get('diory-cart-add-ons')) {
  class DioryCartAddOns extends HTMLElement {
    static busy = false;
    static selection = new Map();

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.tabs = [...this.querySelectorAll('[data-addon-tab]')];
      this.select(DioryCartAddOns.selection.get(this.dataset.context) || this.tabs[0]?.dataset.addonTab);
      this.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-addon-tab]');
        const arrow = event.target.closest('[data-addon-step]');
        const button = event.target.closest('[data-addon-variant]');
        if (tab) this.select(tab.dataset.addonTab);
        if (arrow) {
          const track = this.querySelector('[data-addon-panel]:not([hidden]) .diory-addons__track');
          const card = track?.firstElementChild;
          if (card) track.scrollBy({ left: Number(arrow.dataset.addonStep) * (card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).gap)), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        }
        if (button) this.add(button);
      });
      this.addEventListener('keydown', (event) => {
        const tab = event.target.closest('[data-addon-tab]');
        if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let index = this.tabs.indexOf(tab);
        index = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
        this.select(this.tabs[index].dataset.addonTab);
        this.tabs[index].focus();
      });
      this.querySelectorAll('.diory-addons__track').forEach(track => track.addEventListener('scroll', () => this.updateArrows(), { passive: true }));
      this.resizeObserver = new ResizeObserver(() => this.updateArrows());
      this.resizeObserver.observe(this);
      this.updateArrows();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.initialized = false;
    }

    select(id) {
      if (!this.tabs.some(tab => tab.dataset.addonTab === id)) id = this.tabs[0]?.dataset.addonTab;
      this.tabs.forEach(tab => {
        const selected = tab.dataset.addonTab === id;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      this.querySelectorAll('[data-addon-panel]').forEach(panel => { panel.hidden = panel.dataset.addonPanel !== id; });
      DioryCartAddOns.selection.set(this.dataset.context, id);
      this.updateArrows();
    }

    updateArrows() {
      const track = this.querySelector('[data-addon-panel]:not([hidden]) .diory-addons__track');
      if (!track) return;
      const position = Math.abs(track.scrollLeft);
      const max = track.scrollWidth - track.clientWidth;
      this.querySelector('[data-addon-step="-1"]').disabled = position < 2;
      this.querySelector('[data-addon-step="1"]').disabled = position >= max - 2;
    }

    async add(button) {
      if (DioryCartAddOns.busy) return;
      DioryCartAddOns.busy = true;
      const label = button.textContent;
      const variant = button.dataset.addonVariant;
      const context = this.dataset.context;
      const root = window.Shopify?.routes?.root || '/';
      const drawer = document.querySelector('cart-drawer');
      const main = document.querySelector('cart-items-component');
      const sectionIds = ['cart-icon-bubble'];
      if (drawer) sectionIds.push('cart-drawer');
      if (main?.dataset.sectionId) sectionIds.push(main.dataset.sectionId);
      const scrollTop = drawer?.querySelector('#CartDrawer-Form')?.scrollTop || 0;
      const trackScroll = this.querySelector('[data-addon-panel]:not([hidden]) .diory-addons__track')?.scrollLeft || 0;
      let accepted = false;
      document.querySelectorAll('[data-addon-variant]').forEach(item => { item.disabled = true; });
      button.setAttribute('aria-busy', 'true');
      button.textContent = this.dataset.adding;
      this.querySelector('.diory-addons__status').textContent = '';
      try {
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: variant, quantity: 1 }], sections: sectionIds, sections_url: window.location.pathname })
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || this.dataset.error);
        accepted = true;
        if (!sectionIds.every(id => typeof result.sections?.[id] === 'string')) {
          const url = new URL(window.location.href);
          url.searchParams.set('sections', sectionIds.join(','));
          const refreshed = await fetch(url, { cache: 'no-store' });
          if (!refreshed.ok) throw new Error(this.dataset.refresh);
          result.sections = await refreshed.json();
        }
        if (!sectionIds.every(id => typeof result.sections?.[id] === 'string')) throw new Error(this.dataset.refresh);
        if (main) {
          const incoming = new DOMParser().parseFromString(result.sections[main.dataset.sectionId], 'text/html');
          const selectors = ['.cart-items__wrapper', '.cart-total', '.cart-page__title', 'diory-cart-add-ons[data-context="page"]'];
          const updates = selectors.map(selector => [main.querySelector(selector), incoming.querySelector(selector)]);
          if (updates.some(([current, next]) => !current || !next)) throw new Error(this.dataset.refresh);
          updates.forEach(([current, next]) => current.replaceWith(next));
        }
        if (drawer) {
          drawer.renderContents(result);
        } else {
          const bubble = document.getElementById('cart-icon-bubble');
          const html = new DOMParser().parseFromString(result.sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
          if (bubble && html) bubble.innerHTML = html.innerHTML;
        }
        const cartIcon = document.getElementById('cart-icon-bubble');
        const cartLabel = cartIcon?.querySelector('[data-cart-text]')?.textContent.trim();
        if (cartLabel) cartIcon.setAttribute('aria-label', cartLabel);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'diory-cart-add-ons' } }));
        requestAnimationFrame(() => {
          const current = document.querySelector(`diory-cart-add-ons[data-context="${context}"]`);
          if (!current) return;
          current.querySelector('.diory-addons__status').textContent = this.dataset.added;
          const newTrack = current.querySelector('[data-addon-panel]:not([hidden]) .diory-addons__track');
          if (newTrack) newTrack.scrollLeft = trackScroll;
          const nextButton = current.querySelector(`[data-addon-panel]:not([hidden]) [data-addon-variant="${variant}"]`);
          nextButton?.focus({ preventScroll: true });
          const form = drawer?.querySelector('#CartDrawer-Form');
          if (form) form.scrollTop = scrollTop;
        });
      } catch (error) {
        const current = document.querySelector(`diory-cart-add-ons[data-context="${context}"]`) || this;
        current.querySelector('.diory-addons__status').textContent = accepted ? this.dataset.refresh : error.message || this.dataset.error;
        current.querySelector('.diory-addons__recovery').hidden = !accepted;
      } finally {
        DioryCartAddOns.busy = false;
        document.querySelectorAll('[data-addon-variant]').forEach(item => { item.disabled = false; item.removeAttribute('aria-busy'); });
        if (button.isConnected) button.textContent = label;
      }
    }
  }
  customElements.define('diory-cart-add-ons', DioryCartAddOns);
}
